import { format } from 'date-fns';
import { Building2, Calendar, Clock, User, X, RotateCcw, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { WeatherAlert } from '@/hooks/useWeatherAlerts';

interface WeatherAlertCardProps {
  alert: WeatherAlert;
  onDismiss: () => void;
  onFindIndoor: () => void;
  onReschedule: () => void;
  technicians?: any[];
}

export function WeatherAlertCard({ 
  alert, 
  onDismiss, 
  onFindIndoor, 
  onReschedule,
  technicians = []
}: WeatherAlertCardProps) {
  const getReasonDisplay = (reason: string) => {
    switch (reason) {
      case 'rain':
        return { icon: '🌧️', label: 'Rain Expected', color: 'bg-blue-500/20 text-blue-600' };
      case 'snow':
        return { icon: '❄️', label: 'Snow Expected', color: 'bg-cyan-500/20 text-cyan-600' };
      case 'cold':
        return { icon: '🌡️', label: 'Low Temperature', color: 'bg-purple-500/20 text-purple-600' };
      case 'storm':
        return { icon: '⛈️', label: 'Thunderstorm', color: 'bg-amber-500/20 text-amber-600' };
      case 'heat':
        return { icon: '🔥', label: 'Extreme Heat', color: 'bg-red-500/20 text-red-600' };
      case 'wind':
        return { icon: '💨', label: 'High Wind', color: 'bg-slate-500/20 text-slate-600' };
      default:
        return { icon: '⚠️', label: 'Weather Alert', color: 'bg-warning/20 text-warning' };
    }
  };

  const reasonDisplay = getReasonDisplay(alert.reason);
  const vehicle = alert.ticket?.vehicle;
  const vehicleStr = vehicle 
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` 
    : 'Unknown Vehicle';
  
  const techName = technicians.find(t => t.id === alert.ticket?.assigned_tech_id)?.name || 'Unassigned';
  const bayName = alert.bay?.name || 'Unknown Bay';

  const startTime = new Date(alert.scheduled_start);
  const endTime = new Date(alert.scheduled_end);

  return (
    <Card className="p-4 space-y-3 border-l-4 border-l-warning">
      {/* Header with reason */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xl">{reasonDisplay.icon}</span>
          <Badge variant="outline" className={cn("font-medium", reasonDisplay.color)}>
            {reasonDisplay.label}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Ticket info */}
      <div className="space-y-1.5 text-sm">
        <div className="font-medium">{vehicleStr}</div>
        {alert.ticket?.customer_name && (
          <div className="text-muted-foreground">{alert.ticket.customer_name}</div>
        )}
      </div>

      {/* Details */}
      <div className="grid gap-1.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5" />
          <span>Technician: {techName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5" />
          <span>Bay: {bayName} – Outdoor</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" />
          <span>
            {format(startTime, 'h:mm a')} – {format(endTime, 'h:mm a')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" />
          <span>{format(startTime, 'EEEE, MMM d')}</span>
        </div>
      </div>

      {/*
        CORE-BR provenance.
        This alert was raised from a FORECAST for the job's scheduled hour —
        while `previous_condition` on the same row is the current observation.
        Now that the row records which is which, the card says so instead of
        letting the reader assume the platform watched it happen.
      */}
      <div
        className="rounded-md border border-dashed border-muted-foreground/40 p-2 text-[11px] text-muted-foreground space-y-0.5"
        data-testid={`weather-alert-provenance-${alert.id}`}
        data-observation-kind={alert.observation_kind ?? 'unknown'}
      >
        {alert.observation_kind === 'forecast' ? (
          <>
            <p className="font-semibold uppercase tracking-wide">Forecast</p>
            <p>Alert raised from this forecast</p>
            {alert.weather_effective_at && (
              <p data-testid={`weather-alert-effective-${alert.id}`}>
                Forecast for{' '}
                {format(new Date(alert.weather_effective_at), 'EEE d MMM, h:mm a')}
              </p>
            )}
          </>
        ) : alert.observation_kind === 'current_observation' ? (
          <p className="font-semibold uppercase tracking-wide">
            Observed condition
          </p>
        ) : (
          /*
            A row written before the provenance columns existed. We do not
            know whether it came from an observation or a forecast, and
            guessing would be exactly the invention this capability exists to
            prevent — so it is labelled as unrecorded.
          */
          <p data-testid={`weather-alert-legacy-${alert.id}`}>
            Reading type not recorded for this alert.
          </p>
        )}
        {alert.weather_source && (
          <p data-testid={`weather-alert-source-${alert.id}`}>
            Source: {alert.weather_source}
          </p>
        )}
        {alert.policy_explanation && (
          <p
            className="italic"
            data-testid={`weather-alert-explanation-${alert.id}`}
          >
            {alert.policy_explanation}
          </p>
        )}
      </div>

      {/* Weather condition detail */}
      <div className="bg-muted/50 rounded-md p-2.5 text-xs">
        <span className="text-warning font-medium">⚠️ </span>
        {alert.reason === 'cold' && alert.temperature_f !== null ? (
          <span>
            Forecast used for this alert: {Math.round(alert.temperature_f)}°F during this job's time window
          </span>
        ) : alert.reason === 'rain' ? (
          <span>Forecast used for this alert: rain during this job's time window</span>
        ) : alert.reason === 'snow' ? (
          <span>Forecast used for this alert: snow during this job's time window</span>
        ) : alert.reason === 'storm' ? (
          <span>Forecast used for this alert: thunderstorms during this job's time window</span>
        ) : (
          <span>Forecast used for this alert indicated conditions affecting outdoor work</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onFindIndoor}
        >
          <Building2 className="h-3.5 w-3.5 mr-1.5" />
          Find Indoor
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onReschedule}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          Reschedule
        </Button>
      </div>
    </Card>
  );
}
